import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'zh';

export const translations = {
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      resources: "Resources",
      login: "Log in",
      signup: "Sign up free",
      dashboard: "Dashboard"
    },
    landing: {
      pill: "The First Meeting Minutes AI Agent",
      heroPrefix: "Let meetings become your ",
      heroSuffix: " knowledge asset.",
      heroSubtitle: "Utilizing a multi-expert Agent engine tuned for professional scenarios. Zero hallucinations, bi-directional traceability, and precise capture of fleeting insights.",
      cta: "Start Free Trial",
      mockup: {
        sourceTitle: "Raw Transcript Source",
        reportTitle: "Professional Report View",
        windowTitle: "Minutes AI - Professional Report Generator",
        sarahName: "Sarah (VP)",
        sarahText: "We need to finalize the Q3 infrastructure budget. The cloud costs are spiking.",
        davidName: "David (CTO)",
        davidText: "The main bottleneck is the legacy SQL database. If we scale to 50k users, latency will hit 2 seconds. We must prioritize the Redis caching layer migration immediately.",
        mikeName: "Mike (Product)",
        mikeText: "Does that affect the feature roadmap for next month?",
        docTitle: "Technical Infrastructure Review",
        preparedBy: "Prepared by: Minutes AI",
        summaryTitle: "Executive Summary",
        summaryText: "The team discussed Q3 budget allocation and infrastructure scaling requirements. Key focus areas include cost optimization and legacy system upgrades.",
        riskTitle: "Risk Assessment & Mitigation",
        scalabilityTitle: "Scalability Bottlenecks",
        sqlLimit: "Legacy SQL Limitations: Current architecture cannot support projected user growth (50k+).",
        latencyRisk: "Latency Risk: Projected latency >2s at peak load without intervention.",
        actionTitle: "Required Action",
        highPriority: "High Priority",
        redisAction: "Immediate implementation of Redis Caching Layer to offload database queries and reduce latency.",
        sourceTag: "Source: David (10:12)"
      },
      feature1Title: "Chaos In, Order Out",
      feature1Desc: "Our model deeply understands dialogue logic, extracting structured high-value information directly from chaotic voice records.",
      feature2Title: "Bi-directional Trace",
      feature2Desc: "Every conclusion links back to the exact source audio and transcript segment, ensuring 100% trustworthy and verifiable outputs.",
      feature3Title: "High-Fidelity Editing",
      feature3Desc: "Format with precision without leaving the platform. Enjoy near Word-level rich text typesetting and flawless export capabilities.",
      feature4Title: "Enterprise Data Vault",
      feature4Desc: "Your data is strictly isolated through advanced encryption. SOC2 compliant. We never train on your voice without explicit permission.",
      bento: {
        rawAudioLog: "Raw Audio Log",
        rawLogLine1: "\"Regarding 2025 planning, we should focus on the dormant users.\"",
        rawLogLine2: "\"Agreed. We have years of behavioral data we aren't using. Can we use AI for personalized re-engagement?\"",
        rawLogLine3: "\"Yes, that's exactly the move. Let's make AI-driven User Reactivation our top priority.\"",
        strategicPlan: "Strategic Plan 2025",
        shortTermPriority: "Short-Term Priority",
        reactivationTitle: "Focus 2025 key initiatives on User Reactivation.",
        reactivationDesc: "Leverage accumulated behavioral data to drive targeted re-engagement campaigns.",
        aiApps: "Expand AI Applications for personalization.",
        transcriptHeader: "Raw Transcript",
        alexName: "Alex (CFO)",
        alexText: "TAM is approaching $10B. What's driving this?",
        sarahRole: "Sarah (VP Product)",
        sarahText: "It's the pro tier. Consumer demand for pro features is exceeding expectations.",
        marketAnalysis: "Interview Minutes",
        marketScale: "Key Insight",
        scaleValue: "Users prioritize tool professionalism and efficiency, and are willing to pay for advanced robust features.",
        growthDriver: "Next Steps",
        driverText: "Prioritize the development of Pro features and conduct targeted enterprise user research.",
        verification: "Verification",
        sourceMatched: "Source Matched"
      },
      testimonialsTitle: "Loved by efficient teams",
      testimonialsSub: "Join 10,000+ professionals saving time every day.",
      testimonials: [
        { name: "Sarah J.", role: "Product Manager", text: "I stopped taking notes manually. Minutes AI captures every detail and the auto-tagging is genius." },
        { name: "David L.", role: "CTO @ StartUp", text: "The bidirectional linking between transcripts and my notes changed how I review architecture meetings." },
        { name: "Elena R.", role: "UX Researcher", text: "Finally, a tool that understands context. The summaries aren't just generic bullet points." },
        { name: "Marcus T.", role: "Consultant", text: "It pays for itself in one meeting. The ROI calculator on the site is actually conservative." },
        { name: "Jessica W.", role: "Marketing Lead", text: "Deep search across all my voice notes is a superpower I didn't know I needed." },
        { name: "Tom H.", role: "Founder", text: "Cleanest interface in the game. No clutter, just value." }
      ],
      faqTitle: "Frequently Asked Questions",
      faqSub: "Everything you need to know about MinutesAI.",
      faqs: [
        { q: "What is MinutesAI and how does it work?", a: "MinutesAI is an AI-powered meeting intelligence platform. You upload an audio or video recording of your meeting, and our multi-expert Agent engine automatically transcribes, identifies speakers, extracts key decisions, action items, and generates a structured professional report — all with zero hallucinations and full source traceability." },
        { q: "Which languages and meeting formats are supported?", a: "MinutesAI specializes in Mandarin Chinese and English, with strong support for mixed-language meetings. We handle multi-speaker scenarios, overlapping speech, and technical terminology across industries including finance, legal, consulting, and engineering." },
        { q: "Is my meeting content used to train AI models?", a: "No. We do not allow our AI model providers to use your meeting content or personal data to train their models. MinutesAI may use de-identified, anonymized data to improve our own accuracy — you can opt out at any time in account settings. Enterprise customers have this disabled by default." },
        { q: "How is my data protected?", a: "All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Audio files are deleted immediately after transcription is complete — we never store your raw recordings. Your meeting notes are private by default, and we never sell your data to third parties." },
        { q: "What file formats can I upload?", a: "We support MP3, MP4, WAV, M4A, and most common audio/video formats. Files are automatically converted if needed. There is no need to pre-process your recordings before uploading." },
        { q: "How accurate is the transcription?", a: "Our ASR engine is tuned for professional meeting scenarios with high accuracy on technical terminology, proper nouns, and multi-speaker dialogue. Every output includes bidirectional source tracing — you can click any conclusion to verify the exact audio segment it came from." },
        { q: "Can I try MinutesAI for free?", a: "Yes. New accounts receive free transcription minutes to try the full product with no credit card required. You can upgrade to a paid plan at any time to unlock higher quotas and advanced features." },
        { q: "Does MinutesAI work with Tencent Meeting, DingTalk, or Feishu?", a: "Yes. You can record your meeting from any platform — Tencent Meeting, DingTalk, Feishu, Zoom, Google Meet, or Microsoft Teams — and upload the recording file directly to MinutesAI for processing." }
      ],
      partnersTitle: "Trusted by forward-thinking companies",
      calcTitle: "Calculate your efficiency gains",
      calcDesc: "See exactly how much time and budget Minutes AI can recover for your organization by automating high-fidelity note-taking.",
      teamSize: "Team Size",
      meetingsWeek: "Meetings / Week",
      hourlyRate: "Avg. Hourly Rate",
      annualSavings: "Annual Savings",
      hoursReclaimed: "Hours Reclaimed",
      infoFidelity: "Info Fidelity",
      footerDesc: "The high-fidelity AI workspace for professionals who demand precision, trust, and efficiency."
    },
    dashboard: {
      team: "Minutes AI Team",
      main: "MAIN",
      allRecordings: "All Tasks",
      recent: "Recent",
      favorites: "Favorites",
      folders: "FOLDERS",
      upgrade: "Upgrade to Pro",
      viewPlan: "View my plan",
      freeLabel: "Free",
      proLabel: "Pro",
      planSuffix: "Plan",
      minUsed: "已使用 210 / 300 分钟",
      invite: "Invite & Earn",
      inviteSub: "Get 60 min free",
      logout: "Log out",
      sessionKickedTitle: "Signed Out",
      sessionKickedMessage: "Your account has been signed in on another device. You have been signed out of this session.",
      sessionExpiredTitle: "Session Expired",
      sessionExpiredMessage: "Your session has expired. Please sign in again.",
      sessionConfirm: "Sign In Again",
      searchPlaceholder: "Search transcripts...",
      newRecording: "New Task",
      processing: "Processing",
      proUsed: "已使用 510 / 600 分钟",
      noTasksInFolder: "No files in this folder",
      dragToMove: "Drag files here"
    },
    deleteModal: {
      deleteTitle: "Delete",
      deleteDescription: "You are about to permanently delete",
      cannotUndo: "This action cannot be undone.",
      cancel: "No, Keep It",
      confirm: "Yes, Delete!"
    },
    settings: {
      bindPhone: "Bind Phone",
      bindEmail: "Bind Email",
      phoneNumber: "Phone Number",
      emailAddress: "Email Address",
      verificationCode: "Verification Code",
      sendCode: "Send Code",
      resend: "Resend",
      confirmBind: "Confirm Binding",
      bindSuccess: "Binding successful",
      enterPhone: "Enter phone number",
      enterEmail: "Enter email",
      enterCode: "Enter code",
      bindDesc: "Can be used for login and password recovery"
    },
    workspace: {
      back: "Workspace",
      processed: "Processed",
      template: "Template",
      saved: "Saved",
      save: "Save",
      download: "Download",
      share: "Share",
      transcript: "Transcript",
      aiSummary: "AI Summary",
      readOnly: "Read Only",
      saving: "Saving...",
      loading: "Loading",
      loadingEllipsis: "Loading...",
      fetchFailed: "Failed to load data",
      editNotes: "Edit Notes",
      backButton: "Back",
      searchTranscript: "Search transcript...",
      clickEditSpeaker: "Click to edit speaker",
      editSpeaker: "Edit Speaker",
      enterSpeakerName: "Enter speaker name",
      renameAllSpeaker: "Rename all",
      cancel: "Cancel",
      confirm: "OK",
      switchToChinese: "Switch to Chinese",
      switchToEnglish: "Switch to English",
      preview: "Notes Preview",
      original: "Original",
      edited: "Edited",
      savedStatus: "Saved",
      saveEdits: "Save edits",
      savedCheck: "✓ Saved",
      reset: "Reset",
      exportWordTitle: "Export current content as Word",
      exporting: "Exporting...",
      exportWord: "Export Word",
      exportFailed: "Export failed",
      tutorialAlt: "Editor tutorial",
      tutorialText: "Drag the left handle to reorder paragraphs. Click the right link button to view related source text. Hold from blank area to multi-select.",
      dontRemind: "Don't remind",
      gotIt: "Got it",
      chineseNumbers: "I, II, III",
      viewLinkedSource: "View linked source",
      inputPlaceholder: "Type here..."
    },
    templateCenter: {
      title: "Template Center",
      customizing: "Customizing",
      autoSaved: "Auto-saved",
      done: "Done",
      hierarchyLevels: "Hierarchy Levels",
      levelPrefix: "Level",
      addLevel: "Add Level",
      removeLevel: "Remove Level",
      listFormat: "List Format",
      none: "None",
      number: "Number",
      bullet: "Bullet",
      numberStyle: "Number Style",
      suffix: "Suffix",
      empty: "Empty",
      typography: "Typography",
      fontEastAsia: "Font (East Asia)",
      fontLatin: "Font (Latin)",
      sizeStyle: "Size & Style",
      layoutSpacing: "Layout & Spacing",
      symbolPosition: "Symbol Position",
      textGap: "Text Gap",
      spaceBefore: "Space Before",
      spaceAfter: "Space After",
      unitLines: "lines",
      unitCh: "ch",
      symbol: "Symbol",
      custom: "Custom",
      textIndent: "Text Indent",
      lineSpacing: "Line Spacing",
      lineSingle: "Single (1.0)",
      lineWordDefault: "1.08x (Word Default)",
      line12: "1.2x",
      line15: "1.5x",
      lineDouble: "Double (2.0)",
      schema: "SCHEMA",
      levels: "LEVELS",
      presetTemplates: "Preset Templates",
      myTemplates: "My Templates",
      newTemplate: "New",
      rename: "Rename",
      delete: "Delete",
      confirmDelete: "Delete this template?",
      renameTitle: "Rename Template",
      createTitle: "New Template",
      saveAsTitle: "Save As New Template",
      inputPlaceholder: "Enter template name",
      cancel: "Cancel",
      confirm: "OK",
      creating: "Creating...",
      create: "Create",
      save: "Save",
      saving: "Saving...",
      saved: "Saved",
      apply: "Apply Template",
      applied: "Applied",
      showNotes: "Show Edited Notes",
      showStyle: "Show Template Style",
      showOriginal: "Show Original Notes",
      templateActions: "Template Actions",
      inheritNumbering: "Inherit parent numbering"
    },
    newModal: {
      title: "New File",
      subtitle: "Upload a file or start capturing instantly.",
      sourceFile: "SOURCE FILE",
      upload: "Click to upload",
      dragDrop: "or drag and drop",
      fileLimit: "MP3, M4A, WAV up to 200MB",
      contextTitle: "Context Details",
      estimatedCost: "Estimated Cost",
      usageDeduction: "Usage Deduction",
      costUnit: "Hours",
      deductionUnit: "Mins",
      company: "Company Name",
      industry: "Industry",
      scenarioTitle: "Select Scenario",
      scenarios: {
        podcast: { title: "Podcast", sub: "Interviews, Conversations", desc: "Ideal for podcast interviews and conversations. Auto-extracts key points, speaker insights and discussion highlights." },
        finance: { title: "Financial Interview", sub: "Due diligence, Earnings", desc: "Optimized for earnings calls & due diligence. Highlights numerical data, YoY comparisons & sentiment analysis." },
        qa: { title: "Q&A", sub: "Panels, AMAs, FAQs", desc: "Separates questions from answers automatically. Great for panels, AMAs, and user research sessions." },
        course: { title: "Course", sub: "Classes, Lectures", desc: "Structured for educational content. Generates summaries, key takeaways, and study guides." }
      },
      contextInputs: {
        podcast: { label1: "Content", ph1: "e.g. Meeting agenda, key topics" },
        finance: { label1: "Industry", label2: "Company Name", label3: "Content", ph1: "e.g. Fintech, SaaS", ph2: "e.g. Acme Corp", ph3: "e.g. Q3 earnings call" },
        qa: { label1: "Content", ph1: "e.g. Q4 All Hands, Roadmap discussion" },
        course: { label1: "Content", ph1: "e.g. Macroeconomics lecture notes" }
      },
      autoDetect: "AI will auto-detect speakers based on the selected scenario.",
      startBtn: "Start Transcribing",
      targetLang: "Target Language",
      comingSoon: "Developing more templates... Custom templates coming soon."
    },
    pricing: {
      title: "Choose Your Plan",
      subtitle: "Start for free, scale as you grow",
      monthly: "Monthly",
      yearly: "Yearly",
      save20: "Save 20%",
      mostPopular: "Popular",
      bestDeal: "Best Deal",
      getStarted: "Start Free Trial",
      startFree: "Buy Now",
      contactSales: "Contact Sales",
      annualSaveTag: "Save $96/year",
      teamDiscount: "-20% Team Discount",
      enterpriseOnboarding: "Enterprise Onboarding Included",
      trustTitle: "Enterprise-Grade Trust.",
      trustDesc: "Trusted by 2,500+ top-tier consulting and engineering teams. We offer bank-grade security and a zero-data-training policy without your explicit consent.",
      currencySymbol: "$",
      priceAnchor: "As low as $2.40 / hr (Save 40%)",
      prices: {
        free: "0",
        pro: { monthly: "39.9", yearly: "31.9" },
        team: { monthly: "59", yearly: "47.2" },
        addons: {
          single: "3.99",
          value: { price: "35.00", originalPrice: "39.90", detail: "($3.5/hr)" },
          business: { price: "149.00", originalPrice: "199.50", detail: "($2.98/hr)" }
        }
      },
      plans: {
        basic: {
          name: "Free",
          desc: "Suitable for personal users for light usage."
        },
        pro: {
          name: "Pro",
          desc: "Built for professionals chasing ultimate efficiency."
        },
        team: {
          name: "Team",
          desc: "For teams requiring collaboration and organizational asset accumulation."
        }
      },
      features: {
        basic: [
          "60 min processing time/mo",
          "Top AI Models (Claude, Gemini)",
          "Meeting-grade Agent capabilities",
          "Smart Bi-directional Audio-Text linking",
          "10 Cloud meeting storage limit"
        ],
        pro: [
          "1,000 min Priority processing/mo",
          "Includes all Free plan capabilities",
          "Ready-to-Deliver Word export",
          "Unlimited Cloud document storage",
          "Customizable Agent templates"
        ],
        team: [
          "Includes all Pro plan features",
          "Team Knowledge Base: Cross-meeting Agent deep search",
          "Shared Agent Template library: Unified delivery standards",
          "Admin control & Team efficiency reports"
        ]
      },
      addons: {
        title: "Need more time?",
        desc: "Top up as needed, valid forever.",
        single: "Single Top-up",
        value: "Value Pack (600m)",
        business: "Business Pack (3000m)",
        popular: "Most Popular"
      },
      roi: {
        title: "The 5x Productivity Multiplier",
        desc: "Typically, 1 hour of recording takes 5 hours to organize manually. With Minutes AI, you save those 5 hours instantly.",
        manual: "Manual Work",
        ai: "With Minutes AI",
        highlight: "Save 5 hours per meeting hour"
      }
    },
    auth: {
      welcomeBack: "Welcome Back",
      welcomeSub: "Continue organizing your world with AI-powered clarity.",
      joinTitle: "Join Minutes AI",
      joinSub: "Experience the new standard of professional efficiency.",
      marketing: {
        zeroHallucinations: "Zero hallucinations",
        enterpriseSecurity: "Enterprise security",
        unlimitedRecording: "Unlimited recording"
      },
      login: "Log in",
      createAccount: "Create Account",
      createSub: "Start your 14-day free trial. No credit card required.",
      email: "Email",
      phone: "Phone",
      mobileNumber: "Mobile Number",
      verificationCode: "Verification Code",
      sendCode: "Send Code",
      codeSent: "Code Sent",
      resendIn: "Resend in",
      password: "Password",
      setPassword: "Set Password",
      fullName: "Full Name",
      inviteCode: "Invitation Code (Optional)",
      getCode: "GET 1 MONTH FREE",
      orContinue: "Or continue with",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
      signup: "Sign Up",
      loginTabs: {
        password: "Password",
        emailCode: "Email Code",
        phoneCode: "Phone Code"
      },
      accountPlaceholder: "Email / Phone / Username",
      forgotPassword: "Forgot?",
      forgotPasswordTitle: "Reset Password",
      forgotPasswordDesc: "Enter your email or phone to receive a verification code",
      resetMethod: {
        email: "Reset via Email",
        phone: "Reset via Phone"
      },
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      resetPassword: "Reset Password",
      backToLogin: "Back to Login",
      passwordResetSuccess: "Password reset successfully",
      changePassword: "Change Password",
      recoverAccount: "Recover Your Account",
      securityFirst: "Security First",
      recoverDesc: "Just a few steps to re-secure your digital workspace.",
      multiStepRecovery: "Multi-step Recovery",
      multiStepRecoveryDesc: "We use your linked email or phone for dual verification, ensuring only you can reset your access.",
      instantRecovery: "Instant Recovery",
      instantRecoveryDesc: "Once reset, your new password takes effect immediately. Return to your insights in no time.",
      secureProtocol: "Secure protocol protected by Minutes AI",
      verifyIdentity: "Verify Identity",
      setNewPassword: "Set New Password",
      passwordChanged: "Password Changed",
      resetSuccessful: "Reset Successful",
      chooseResetMethod: "Choose a method to reset your password",
      chooseChangeMethod: "Choose a method to change your password",
      codeSentTo: "Code sent to",
      setNewPasswordDesc: "Please set your new password",
      passwordChangedDesc: "Your password has been changed successfully",
      passwordResetDesc: "Your password has been reset successfully",
      sendCodeBtn: "Send Code",
      rememberPassword: "Remember it? Back to login",
      resend: "Resend",
      nextStep: "Next Step",
      resetNow: "Reset Now",
      successTitle: "Success!",
      successChangeDesc: "Your password has been changed successfully. Please use your new password.",
      successResetDesc: "Account re-secured. Your new password is now active. You can now log back in.",
      fillAllFields: "Please fill in all fields",
      sendFailed: "Failed to send",
      enterEmail: "Please enter email",
      enterPhone: "Please enter phone number",
      enterCode: "Please enter verification code",
      invalidCode: "Invalid verification code",
      enterNewPassword: "Please enter new password",
      passwordMinLength: "Password must be at least 6 characters",
      passwordMismatch: "Passwords do not match",
      resetFailed: "Reset failed",
      userNotFound: "User not found, please sign up",
      loginFailed: "Login failed",
      registrationFailed: "Registration failed",
      inviteCodeHint: "Both you and your friend get 60 min!",
      codeSendFailed: "Failed to send code",
      fillAllRequired: "Please fill in all required fields",
      whatsNew: "What's New",
      march2025: "March 2026",
      systemsOperational: "Systems Operational",
      insightsWaiting: "Your meeting insights are waiting for you.",
      releaseNotes: "View Release Notes",
      consultingEngineTitle: "Consulting-Grade Synthesis v2.4",
      consultingEngineDesc: "Our latest reasoning engine upgrade significantly improves technical terminology accuracy and multi-speaker overlapping detection.",
      testimonials: [
        {
          name: "Alex Wang",
          role: "Investment Manager",
          company: "Top Tier VC",
          text: "Minutes AI extracts pure signal from noise. It makes reviewing founder interviews and board meetings exponentially more valuable for our deal flow.",
          avatar: "/images/avatar1.png"
        },
        {
          name: "Elena Rodriguez",
          role: "Legal Counsel",
          company: "TechFlow Systems",
          text: "The absolute fidelity and zero hallucinations give me the confidence to use these summaries in professional filings without manual checking.",
          avatar: "/images/avatar2.png"
        }
      ],
      faqTitle: "Frequently Asked Questions",
      faqSub: "Everything you need to know about MinutesAI.",
      faqs: [
        {
          q: "What is MinutesAI and how does it work?",
          a: "MinutesAI is an AI-powered meeting intelligence platform. You upload an audio or video recording of your meeting, and our multi-expert Agent engine automatically transcribes, identifies speakers, extracts key decisions, action items, and generates a structured professional report — all with zero hallucinations and full source traceability."
        },
        {
          q: "Which languages and meeting formats are supported?",
          a: "MinutesAI specializes in Mandarin Chinese and English, with strong support for mixed-language meetings. We handle multi-speaker scenarios, overlapping speech, and technical terminology across industries including finance, legal, consulting, and engineering."
        },
        {
          q: "Is my meeting content used to train AI models?",
          a: "No. We do not allow our AI model providers to use your meeting content or personal data to train their models. MinutesAI may use de-identified, anonymized data to improve our own accuracy — you can opt out at any time in account settings. Enterprise customers have this disabled by default."
        },
        {
          q: "How is my data protected?",
          a: "All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Audio files are deleted immediately after transcription is complete — we never store your raw recordings. Your meeting notes are private by default, and we never sell your data to third parties."
        },
        {
          q: "What file formats can I upload?",
          a: "We support MP3, MP4, WAV, M4A, and most common audio/video formats. Files are automatically converted if needed. There is no need to pre-process your recordings before uploading."
        },
        {
          q: "How accurate is the transcription?",
          a: "Our ASR engine is tuned for professional meeting scenarios with high accuracy on technical terminology, proper nouns, and multi-speaker dialogue. Every output includes bidirectional source tracing — you can click any conclusion to verify the exact audio segment it came from."
        },
        {
          q: "Can I try MinutesAI for free?",
          a: "Yes. New accounts receive free transcription minutes to try the full product with no credit card required. You can upgrade to a paid plan at any time to unlock higher quotas and advanced features."
        },
        {
          q: "Does MinutesAI work with Tencent Meeting, DingTalk, or Feishu?",
          a: "Yes. You can record your meeting from any platform — Tencent Meeting, DingTalk, Feishu, Zoom, Google Meet, or Microsoft Teams — and upload the recording file directly to MinutesAI for processing."
        }
      ]
    },
    gateModal: {
      title: "Join Minutes AI Today",
      subtitle: "Create unlimited structured minutes for your work with our rich collection of templates.",
      createAccount: "Create Your Account",
      orSignIn: "Or Sign In"
    },
    inviteModal: {
      title: "Invite Friends, Get 60 Min",
      desc: "Invite friends to register, both get 60 min free!",
      yourCode: "Your Invite Code",
      tapToCopy: "TAP TO COPY",
      copied: "COPIED",
      orShareLink: "Or share link",
      terms: "Terms & Conditions apply",
      referrals: "0 referrals so far"
    },
    share: {
      title: "Share Recording",
      publicAccess: "Public Access",
      publicDesc: "Anyone with the link can view this transcript.",
      privateDesc: "Only invited team members can access.",
      copyLink: "Copy Link",
      copied: "Copied",
      preview: "Preview",
      previewTooltip: "See what they see",
      canView: "Can view",
      canEdit: "Can edit",
      disclaimer: "By sharing this link, you agree to our Terms of Service regarding content distribution."
    },
    public: {
      sharedBy: "Shared by",
      join: "Join the conversation",
      joinDesc: "Sign up to edit, comment, and export this meeting.",
      signupFree: "Sign Up Free",
      wantAction: "Want to see the action items?",
      login: "Log in",
      signup: "Sign Up"
    },
    paymentModal: {
      chooseMethod: "Choose Payment Method",
      detectingRegion: "Detecting your region...",
      regionMainland: "Mainland China",
      regionOverseas: "Overseas",
      selectMethod: "Select payment method",
      wechatPay: "WeChat Pay",
      alipay: "Alipay",
      wechatDesc: "Scan QR code with WeChat",
      alipayDesc: "Redirect to Alipay payment page",
      confirmPayment: "Confirm Payment",
      cancel: "Cancel",
      detecting: "Detecting...",
      detectFailed: "Region detection failed, using defaults",
      qrScanScan: "Please scan the QR code below to complete payment",
      qrOrderNo: "Order: ",
      qrUpdateAuto: "Page will update after payment",
      qrFinished: "I have paid",
      purchaseFailed: "Purchase Failed",
      purchaseSuccess: "Purchase Successful",
      proActivated: "Pro membership activated",
      alipayRedirect: "Redirected to Alipay payment page, please complete payment in the new window",
      processing: "Processing...",
      buyNow: "Buy Now",
      purchaseSuccessDuration: "Purchase successful! Duration package added",
      purchaseFailedFallback: "Purchase failed",
      customPlan: "Custom Plan",
      customPlanDesc: "Tailored to your team size, workflow complexity, and compliance requirements.",
      accuracyGuaranteed: "Accuracy Guaranteed",
      bankLevelSec: "Bank-level Sec",
      bankLevelSecSub: "SOC2 & AES-256",
      accuracySub: "Accuracy Guaranteed",
      contactTitle: "Contact Us",
      contactDesc: "Team plans require custom setup. Please contact us via email for dedicated service.",
      copy: "Copy",
      copied: "Copied",
      close: "Close",
      wechatScanPay: "WeChat Pay",
      alipayScanPay: "Alipay",
      purchaseSuccessPro: "Purchase successful! Pro membership activated",
      purchaseSuccessTeam: "Purchase successful! Team membership activated",
      back: "Back",
      upgradePlan: "Upgrade Plan",
      currentPlanLabel: "Current Plan",
      currentPlan: "Current Plan",
      renew: "Renew",
      mainlandChina: "Mainland China",
      overseas: "Overseas",
      scanToPay: "Scan to pay",
      redirectToPay: "Redirect to pay",
      alipayRedirectPending: "Redirected to Alipay. Please complete payment in the new window.",
      enableNotifications: "Enable Notifications",
      enableNotificationsDesc: "Get notified when your tasks complete.",
      enable: "Enable",
      notNow: "Not Now",
      retry: "Retry",
      memberMonthly: "Monthly",
      memberPermanent: "Permanent Quota",
      memberUnlimited: "Unlimited",
      manage: "Manage",
      expiresLabel: "Expires:",
      daysLeft: "d left",
      cofounderExclusive: "Exclusive Beta",
      cofounderWelcome: "Welcome, ",
      cofounderLifetime: "Lifetime Benefit",
      cofounderDiscount: "Exclusive discount on all plans",
      cofounderViewBenefits: "View Full Benefits",
      cofounderMaybeLater: "Maybe Later",
      cofounderDesc: "Thank you for joining MinutesAI early. As one of our first 100 founding users, you have automatically unlocked a permanent 20% discount on all purchases."
    }
  },
  zh: {
    nav: {
      features: "功能特性",
      pricing: "价格方案",
      resources: "资源中心",
      login: "登录",
      signup: "免费注册",
      dashboard: "控制台"
    },
    landing: {
      pill: "首款会议纪要 AI Agent",
      heroPrefix: "让会议成为您的 ",
      heroSuffix: " 知识资产",
      heroSubtitle: "我们专为高信息密度会议场景设计了多专家Agent引擎，实现 0 幻觉、信息双向溯源，精准捕捉转瞬即逝的关键信息。",
      cta: "开始免费试用",
      mockup: {
        sourceTitle: "原始转写流",
        reportTitle: "专业级报告视图",
        windowTitle: "Minutes AI - 专业级会议纪要生成器",
        sarahName: "Sarah (副总裁)",
        sarahText: "我们需要最终确定 Q3 的基础设施预算. 云成本正在飙升。",
        davidName: "David (首席技术官)",
        davidText: "核心瓶颈在于当前的 SQL 数据库集群架构. 根据压力测试结果，一旦用户规模突破 5 万大关，查询延迟将飙升至 2 秒以上。我们必须立即将 Redis 缓存层的迁移列为 Q3 最高优先级任务。",
        mikeName: "Mike (产品经理)",
        mikeText: "这会影响下个月的功能路线图吗？",
        docTitle: "技术基础设施架构评审",
        preparedBy: "生成方：Minutes AI",
        summaryTitle: "执行摘要",
        summaryText: "本次会议核心探讨了 Q3 季度的预算分配细节与基础设施的长期扩展需求. 管理层达成共识，将重点投入成本结构优化以及老旧系统的平滑升级，为接下来的爆发增长期做好充分支撑。",
        riskTitle: "风险评估与缓解方案",
        scalabilityTitle: "可扩展性瓶颈",
        sqlLimit: "旧版 SQL 限制：当前架构无法支持预计的 5 万+ 用户增长。",
        latencyRisk: "延迟风险：若不干预，峰值负载下的预计延迟将超过 2 秒。",
        actionTitle: "必要行动项",
        highPriority: "高优先级",
        redisAction: "立即实施 Redis 缓存层以减轻数据库查询压力并降低延迟。",
        sourceTag: "来源：David (10:12)"
      },
      feature1Title: "化繁为简",
      feature1Desc: "我们的模型能深度理解对话逻辑，从混乱的语音记录中直接提取结构化的高价值信息。乱序输入，结构输出。",
      feature2Title: "原文双向溯源",
      feature2Desc: "纪要中的每一个结论都可以精准定位并跳转至对应的原始语音段落和逐字稿源头，确保结论 100% 可信。",
      feature3Title: "高保真深度编辑",
      feature3Desc: "无需离开平台即可进行格式精准调控。提供接近 Word 级别的原生富文本排版能力，并支持完美的高保真导出。",
      feature4Title: "企业级数据保险箱",
      feature4Desc: "您的数据通过先进的加密技术被严格隔离。完全符合 SOC2 标准规范，未经明确授权，绝不使用您的语音数据进行模型训练。",
      bento: {
        rawAudioLog: "原始音频日志",
        rawLogLine1: "“关于 2025 年的规划，我觉得核心焦点应该放在老用户的促活上。”",
        rawLogLine2: "“同意. 我们积累了多年的行为数据一直没用上. 能不能用 AI 做精准的个性化再触达？”",
        rawLogLine3: "“没错，这正是我们要做的. 把‘AI 驱动的用户激活’列为年度最高优先级。”",
        strategicPlan: "2025 战略规划",
        shortTermPriority: "短期优先级",
        reactivationTitle: "2025 年核心计划：用户激活。",
        reactivationDesc: "利用积累的行为数据驱动精准的再触达营销活动。",
        aiApps: "扩大 AI 在个性化推荐中的应用。",
        transcriptHeader: "原始逐字稿",
        alexName: "Alex (首席财务官)",
        alexText: "可触达市场规模正逼近 100 亿美元. 是什么在驱动这一点？",
        sarahRole: "Sarah (产品副总裁)",
        sarahText: "是专业版层级. 消费者对专业功能的需求超出了预期。",
        marketAnalysis: "访谈会议纪要",
        marketScale: "核心观点",
        scaleValue: "用户更看重工具的专业性与效率，愿意为高级功能单独付费。",
        growthDriver: "后续行动",
        driverText: "优先排期专业版功能的研发，并在一线员工中开展定向用户调研。",
        verification: "真实性验证",
        sourceMatched: "已匹配原始数据源"
      },
      testimonialsTitle: "深受高效团队喜爱",
      testimonialsSub: "加入 10,000+ 专业人士，每天节省时间。",
      testimonials: [
        { name: "陈女士 (匿名)", role: "某出海品牌 市场总监", text: "真正做到了开箱即用。不仅会议记录全面，无幻觉的双向溯源让我汇报时底气实足。" },
        { name: "王先生", role: "资深投研分析师", text: "访谈和财报分析的利器。能够精准抓取大量数据和图表关系，至少帮我省了三倍的整理时间。" },
        { name: "林女士", role: "企业架构师", text: "我们的技术评审会通常有很多人发言，多语言和重叠收音也能完美识别，逻辑架构整理得很清晰。" },
        { name: "刘总", role: "SaaS 创始人", text: "对齐目标神器。现在开完管理团队周会，立刻生成执行清单发给各个 Leader，项目进度明显加快。" },
        { name: "赵先生", role: "高级咨询顾问", text: "高保真深度编辑功能深得我心，几乎就是在平台里排版，直接导出 Word 给客户，非常体面。" },
        { name: "张经理", role: "产品研发负责人", "text": "这是我用过最能理解上下文的 AI 工具，摘要不是干瘪的点列，而是有商业深度的判断。" }
      ],
      partnersTitle: "深受全球前沿企业信赖",
      calcTitle: "计算您的效率收益",
      calcDesc: "看看 Minutes AI 通过自动化高保真笔记，能为您的组织挽回多少时间和预算。",
      teamSize: "团队规模",
      meetingsWeek: "每周会议",
      hourlyRate: "平均时薪",
      annualSavings: "年度节省",
      hoursReclaimed: "挽回工时",
      infoFidelity: "信息保真度",
      footerDesc: "专为追求精准、信任和效率的专业人士打造的高保真 AI 工作台。",
      faqTitle: "常见问题",
      faqSub: "关于 MinutesAI，您想知道的都在这里。",
      faqs: [
        { q: "MinutesAI 是什么，它是如何工作的？", a: "MinutesAI 是一款 AI 驱动的会议智能平台。您上传会议录音或视频，我们的多专家 Agent 引擎会自动完成转录、说话人识别、关键决策和行动项提取，并生成结构化专业报告——零幻觉，全程双向溯源。" },
        { q: "支持哪些语言和会议场景？", a: "MinutesAI 专为普通话和英语优化，对中英混合会议有强力支持。我们能处理多说话人、重叠发言和各行业专业术语，覆盖金融、法律、咨询、工程等场景。" },
        { q: "我的会议内容会被用于训练 AI 模型吗？", a: "不会。我们不允许 AI 模型提供商使用您的会议内容或个人数据训练其模型。MinutesAI 可能使用去标识化的匿名数据提升自身准确率——您可随时在账户设置中选择退出。企业客户默认关闭此功能。" },
        { q: "我的数据如何被保护？", a: "所有数据在传输中使用 TLS 1.3 加密，静态存储使用 AES-256 加密。音频文件在转录完成后立即删除，我们从不保留您的原始录音。会议纪要默认私密，我们绝不向第三方出售您的数据。" },
        { q: "支持上传哪些文件格式？", a: "支持 MP3、MP4、WAV、M4A 及大多数常见音视频格式，无需预处理，上传后自动转换。" },
        { q: "转录准确率如何？", a: "我们的 ASR 引擎专为专业会议场景调优，对技术术语、专有名词和多人对话有极高准确率。每条输出均支持双向溯源——点击任意结论即可定位到对应的原始音频片段。" },
        { q: "可以免费试用吗？", a: "可以。新账户注册即获赠免费转录时长，无需绑定信用卡，即可体验完整功能。随时可升级付费套餐以解锁更高配额和高级功能。" },
        { q: "支持腾讯会议、钉钉、飞书的录音吗？", a: "支持。您可以从任意平台录制会议——腾讯会议、钉钉、飞书、Zoom、Google Meet 或 Microsoft Teams——然后将录音文件直接上传至 MinutesAI 进行处理。" }
      ]
    },
    dashboard: {
      team: "Minutes AI 团队",
      main: "主要",
      allRecordings: "全部任务",
      recent: "最近",
      favorites: "收藏",
      folders: "文件夹",
      upgrade: "升级专业版",
      viewPlan: "查看我的计划",
      freeLabel: "Free",
      proLabel: "Pro",
      planSuffix: "计划",
      minUsed: "已使用 210 / 300 分钟",
      invite: "邀请有礼",
      inviteSub: "获赠 60 分钟时长",
      logout: "退出登录",
      sessionKickedTitle: "账号已下线",
      sessionKickedMessage: "您的账号已在其他设备登录，当前会话已失效。",
      sessionExpiredTitle: "登录已过期",
      sessionExpiredMessage: "您的登录状态已过期，请重新登录。",
      sessionConfirm: "重新登录",
      searchPlaceholder: "搜索逐字稿...",
      newRecording: "新建任务",
      processing: "处理中",
      proUsed: "已使用 510 / 600 分钟",
      noTasksInFolder: "此文件夹暂无文件",
      dragToMove: "拖拽文件到此处"
    },
    deleteModal: {
      deleteTitle: "删除",
      deleteDescription: "您即将永久删除",
      cannotUndo: "此操作无法撤销。",
      cancel: "取消",
      confirm: "确认删除"
    },
    settings: {
      bindPhone: "绑定手机号",
      bindEmail: "绑定邮箱",
      phoneNumber: "手机号",
      emailAddress: "邮箱地址",
      verificationCode: "验证码",
      sendCode: "发送验证码",
      resend: "重新发送",
      confirmBind: "确认绑定",
      bindSuccess: "绑定成功",
      enterPhone: "请输入手机号",
      enterEmail: "请输入邮箱",
      enterCode: "请输入验证码",
      bindDesc: "绑定后可用于登录和找回密码"
    },
    workspace: {
      back: "工作台",
      processed: "已处理",
      template: "模板",
      saved: "已保存",
      save: "保存",
      download: "下载",
      share: "分享",
      transcript: "逐字稿",
      aiSummary: "AI 智能摘要",
      readOnly: "只读模式",
      saving: "保存中...",
      loading: "加载中",
      loadingEllipsis: "加载中...",
      fetchFailed: "获取数据失败",
      editNotes: "编辑纪要",
      backButton: "返回",
      searchTranscript: "搜索逐字稿...",
      clickEditSpeaker: "点击编辑说话人",
      editSpeaker: "编辑说话人",
      enterSpeakerName: "输入说话人名称",
      renameAllSpeaker: "修改所有",
      cancel: "取消",
      confirm: "确认",
      switchToChinese: "切换到中文",
      switchToEnglish: "切换到英文",
      preview: "纪要预览",
      original: "原始版",
      edited: "编辑版",
      savedStatus: "已保存",
      saveEdits: "保存编辑内容",
      savedCheck: "✓ 已保存",
      reset: "重置",
      exportWordTitle: "导出当前内容为Word文档",
      exporting: "导出中...",
      exportWord: "导出Word",
      exportFailed: "导出失败",
      tutorialAlt: "编辑器教学",
      tutorialText: "拖拽左侧手柄可调整段落顺序，点击右侧连线按钮可查看关联原文。从空白处按住可多选。",
      dontRemind: "不再提醒",
      gotIt: "知道了",
      chineseNumbers: "一, 二, 三",
      viewLinkedSource: "查看关联原文",
      inputPlaceholder: "输入内容..."
    },
    templateCenter: {
      title: "模板中心",
      customizing: "当前编辑",
      autoSaved: "已自动保存",
      done: "完成",
      hierarchyLevels: "层级设置",
      levelPrefix: "层级",
      addLevel: "添加层级",
      removeLevel: "移除层级",
      listFormat: "列表格式",
      none: "无",
      number: "编号",
      bullet: "项目符号",
      numberStyle: "编号样式",
      suffix: "后缀",
      empty: "无",
      typography: "字体排版",
      fontEastAsia: "中文字体",
      fontLatin: "西文字体",
      sizeStyle: "大小与样式",
      layoutSpacing: "布局与间距",
      symbolPosition: "符号位置",
      textGap: "文本间距",
      spaceBefore: "段前间距",
      spaceAfter: "段后间距",
      unitLines: "行",
      unitCh: "字符",
      symbol: "符号",
      custom: "自定义",
      textIndent: "文本缩进",
      lineSpacing: "行距",
      lineSingle: "单倍 (1.0)",
      lineWordDefault: "1.08倍 (Word 默认)",
      line12: "1.2倍",
      line15: "1.5倍",
      lineDouble: "双倍 (2.0)",
      schema: "模板方案",
      levels: "层级",
      presetTemplates: "预设模板",
      myTemplates: "我的模板",
      newTemplate: "新建模板",
      rename: "重命名",
      delete: "删除",
      confirmDelete: "确定删除此模板？",
      renameTitle: "重命名模板",
      createTitle: "新建模板",
      saveAsTitle: "另存为新模板",
      inputPlaceholder: "输入模板名称",
      cancel: "取消",
      confirm: "确定",
      creating: "创建中...",
      create: "创建",
      save: "保存",
      saving: "保存中...",
      saved: "已保存",
      apply: "应用模板",
      applied: "已应用",
      showNotes: "展示编辑版纪要",
      showStyle: "展示模板样式",
      showOriginal: "展示原始版纪要",
      templateActions: "模板操作",
      inheritNumbering: "继承上级编号"
    },
    newModal: {
      title: "新建任务",
      subtitle: "上传文件或立即开始录制。",
      sourceFile: "原始文件",
      upload: "点击上传",
      dragDrop: "或拖拽文件至此",
      fileLimit: "支持 MP3, M4A, WAV 最大 200MB",
      contextTitle: "上下文详情",
      estimatedCost: "预计消耗",
      usageDeduction: "扣除时长",
      costUnit: "小时",
      deductionUnit: "分钟",
      company: "公司名称",
      industry: "所属行业",
      scenarioTitle: "从选择你的模板开始吧...",
      scenarios: {
        podcast: { title: "播客", sub: "访谈, 对话", desc: "适合播客访谈和对话场景。自动提取关键观点、嘉宾见解和讨论要点。" },
        finance: { title: "金融访谈", sub: "尽职调查, 财报分析", desc: "针对财报电话会议和尽职调查进行优化。突出显示数值数据、同比比较和情绪分析。" },
        qa: { title: "问答", sub: "论坛, 访谈", desc: "自动分离问题与回答。非常适合专家面板、AMA 问答和用户研究访谈。" },
        course: { title: "课程", sub: "公开课, 讲座", desc: "专为教育内容结构化设计。生成摘要、关键知识点和学习指南。" }
      },
      contextInputs: {
        podcast: { label1: "内容", ph1: "例如：会议议程、关键话题" },
        finance: { label1: "所属行业", label2: "公司名称", label3: "内容", ph1: "例如：金融科技, SaaS", ph2: "例如：Acme Corp", ph3: "例如：Q3财报电话会" },
        qa: { label1: "内容", ph1: "例如：Q4 全员大会、路线图讨论" },
        course: { label1: "内容", ph1: "例如：宏观经济课程笔记" }
      },
      autoDetect: "AI 将根据所选场景自动检测发言人。",
      startBtn: "开始转写",
      targetLang: "输出目标语言",
      comingSoon: "更多模板正在开发中... 自定义模板即将上线。"
    },
    pricing: {
      title: "简单，透明的定价",
      subtitle: "免费开始，随需扩展",
      monthly: "月付",
      yearly: "年付",
      save20: "节省 20%",
      mostPopular: "最受欢迎",
      bestDeal: "最划算",
      getStarted: "开始免费试用",
      startFree: "购买",
      contactSales: "联系销售",
      annualSaveTag: "每年立省 ¥324",
      teamDiscount: "-20% 团队优惠",
      enterpriseOnboarding: "包含企业级入职服务",
      trustTitle: "企业级信任保障",
      trustDesc: "深受上千家顶尖咨询与金融机构的专业人士信赖. 我们提供严苛的安全审计保障，且未经您的允许绝不使用您的数据进行 AI 训练。",
      currencySymbol: "¥",
      priceAnchor: "低至 ¥8.4 / 小时 (对比单买节省 40%)",
      prices: {
        free: "0",
        pro: { monthly: "139", yearly: "112" },
        team: { monthly: "199", yearly: "159" },
        addons: {
          single: "13.9",
          value: { price: "119.0", originalPrice: "139.0", detail: "(约 ¥11.9 / 60min)" },
          business: { price: "499.0", originalPrice: "695.0", detail: "(约 ¥9.9 / 60min)" }
        }
      },
      plans: {
        basic: {
          name: "Free",
          desc: "适合个人用户轻度使用。"
        },
        pro: {
          name: "Pro",
          desc: "为追求极致效率的职场专业人士打造。"
        },
        team: {
          name: "Team",
          desc: "适用于需要团队协作与组织资产沉淀的场景。"
        }
      },
      features: {
        basic: [
          "每月 60 分钟处理时长",
          "顶级AI模型能力 (Claude，Gemini)",
          "专为高质量会议设计的 Agent 能力",
          "智能音文双向溯源",
          "10 篇云端会议存储限制"
        ],
        pro: [
          "每月 1,000 分钟优先处理时长",
          "包含基础版全部能力",
          "即刻交付级 Word 导出",
          "无限量云端文档存储",
          "自定义 Agent 模板"
        ],
        team: [
          "包含所有专业版功能",
          "团队知识库: 支持跨会议 Agent 深度搜索",
          "共享 Agent 模板库: 统一全员交付标准",
          "管理员权限控制与团队效能分析报告"
        ]
      },
      addons: {
        title: "需要更多时长？",
        desc: "按需充值，永久有效。",
        single: "单次加油 (60m)",
        value: "超值包 (600m)",
        business: "商务包 (3000m)",
        popular: "最受欢迎"
      },
      roi: {
        title: "5倍生产力倍增器",
        desc: "一般来说，一小时的录音需要 5 小时整理. 使用 Minutes AI，您可以为每小时的会议节省 5 小时的整理时间。",
        manual: "人工整理",
        ai: "使用 Minutes AI",
        highlight: "每小时会议节省 5 小时"
      }
    },
    auth: {
      welcomeBack: "欢迎回来",
      welcomeSub: "用 AI 驱动的清晰度继续整理您的世界。",
      joinTitle: "Join Minutes AI",
      joinSub: "体验专业效率的新标准。",
      marketing: {
        zeroHallucinations: "零幻觉准确率",
        enterpriseSecurity: "企业级安全",
        unlimitedRecording: "无限录音时长"
      },
      login: "登录",
      createAccount: "创建账户",
      createSub: "开始免费试用，无需绑定信用卡",
      email: "电子邮箱",
      phone: "手机号码",
      mobileNumber: "手机号码",
      verificationCode: "验证码",
      sendCode: "发送验证码",
      codeSent: "已发送",
      resendIn: "重新发送",
      password: "密码",
      setPassword: "设置密码",
      fullName: "全名",
      inviteCode: "邀请码 (选填)",
      getCode: "获赠 1 个月免费",
      orContinue: "或继续使用",
      noAccount: "还没有账户？",
      hasAccount: "已有账户？",
      signup: "注册",
      loginTabs: {
        password: "密码登录",
        emailCode: "邮箱验证码",
        phoneCode: "手机验证码"
      },
      accountPlaceholder: "邮箱 / 手机号 / 用户名",
      forgotPassword: "忘记密码?",
      forgotPasswordTitle: "重置密码",
      forgotPasswordDesc: "输入您的邮箱或手机号以接收验证码",
      resetMethod: {
        email: "邮箱验证",
        phone: "手机验证"
      },
      newPassword: "新密码",
      confirmPassword: "确认密码",
      resetPassword: "重置密码",
      backToLogin: "返回登录",
      passwordResetSuccess: "密码重置成功",
      changePassword: "修改密码",
      recoverAccount: "找回您的账户",
      securityFirst: "安全第一",
      recoverDesc: "只需简单几步，即可重新锁定您的数字资产。",
      multiStepRecovery: "多重验证",
      multiStepRecoveryDesc: "我们通过您的绑定邮箱或手机号进行双重校验，确保只有您本人能修改密码。",
      instantRecovery: "即时响应",
      instantRecoveryDesc: "重置成功后，新的密码将立即生效，您可即刻返回会议紀要世界。",
      secureProtocol: "安全协议由 Minutes AI 保护",
      verifyIdentity: "验证身份",
      setNewPassword: "设置新密码",
      passwordChanged: "修改成功",
      resetSuccessful: "重置成功",
      chooseResetMethod: "选择验证方式找回您的密码",
      chooseChangeMethod: "选择验证方式修改您的密码",
      codeSentTo: "验证码已发送至",
      setNewPasswordDesc: "请设置您的新密码",
      passwordChangedDesc: "您的密码已修改成功",
      passwordResetDesc: "您的密码已重置成功",
      sendCodeBtn: "发送验证码",
      rememberPassword: "想起密码？点击登录",
      resend: "重发",
      nextStep: "下一步",
      resetNow: "立即找回",
      successTitle: "处理完成",
      successChangeDesc: "您的密码已成功修改。下次请使用您的新密码进行登录。",
      successResetDesc: "账户已重认证。您的新密码已生效，现在可以返回登录。",
      fillAllFields: "请填写完整信息",
      sendFailed: "发送失败",
      enterEmail: "请输入邮箱",
      enterPhone: "请输入手机号",
      enterCode: "请输入验证码",
      invalidCode: "验证码格式不正确",
      enterNewPassword: "请输入新密码",
      passwordMinLength: "密码至少6位",
      passwordMismatch: "两次密码不一致",
      resetFailed: "重置失败",
      userNotFound: "用户不存在，请先注册",
      loginFailed: "登录失败",
      registrationFailed: "注册失败",
      inviteCodeHint: "使用邀请码注册，双方各得60分钟时长！",
      codeSendFailed: "发送验证码失败",
      fillAllRequired: "请填写所有必填项",
      whatsNew: "最新动态",
      march2025: "2026年3月",
      systemsOperational: "系统运行正常",
      insightsWaiting: "您的会议洞察正在等您。",
      releaseNotes: "查看更新日志",
      consultingEngineTitle: "咨询级逻辑提取引擎 v2.4",
      consultingEngineDesc: "我们最新的推理引擎升级显著提高了技术术语的准确性和多发言人重叠检测能力。",
      testimonials: [
        {
          name: "Alex Wang",
          role: "投资经理",
          company: "某知名 VC",
          text: "它能从杂音中精准提取信号。无论是复盘创始人访谈还是董事会决议，都能让我们的项目信息萃取效率产生质的飞跃。",
          avatar: "/images/avatar1.png"
        },
        {
          name: "Elena Rodriguez",
          role: "法律顾问",
          company: "TechFlow Systems",
          text: "绝对的高保真度和零幻觉准确率让我有信心将这些摘要直接用于专业法律文件，而无需手动反复核对。",
          avatar: "/images/avatar2.png"
        }
      ],
      faqTitle: "常见问题",
      faqSub: "关于 MinutesAI，您想知道的都在这里。",
      faqs: [
        {
          q: "MinutesAI 是什么，它是如何工作的？",
          a: "MinutesAI 是一款 AI 驱动的会议智能平台。您上传会议录音或视频，我们的多专家 Agent 引擎会自动完成转录、说话人识别、关键决策和行动项提取，并生成结构化专业报告——零幻觉，全程双向溯源。"
        },
        {
          q: "支持哪些语言和会议场景？",
          a: "MinutesAI 专为普通话和英语优化，对中英混合会议有强力支持。我们能处理多说话人、重叠发言和各行业专业术语，覆盖金融、法律、咨询、工程等场景。"
        },
        {
          q: "我的会议内容会被用于训练 AI 模型吗？",
          a: "不会。我们不允许 AI 模型提供商使用您的会议内容或个人数据训练其模型。MinutesAI 可能使用去标识化的匿名数据提升自身准确率——您可随时在账户设置中选择退出。企业客户默认关闭此功能。"
        },
        {
          q: "我的数据如何被保护？",
          a: "所有数据在传输中使用 TLS 1.3 加密，静态存储使用 AES-256 加密。音频文件在转录完成后立即删除，我们从不保留您的原始录音。会议纪要默认私密，我们绝不向第三方出售您的数据。"
        },
        {
          q: "支持上传哪些文件格式？",
          a: "支持 MP3、MP4、WAV、M4A 及大多数常见音视频格式，无需预处理，上传后自动转换。"
        },
        {
          q: "转录准确率如何？",
          a: "我们的 ASR 引擎专为专业会议场景调优，对技术术语、专有名词和多人对话有极高准确率。每条输出均支持双向溯源——点击任意结论即可定位到对应的原始音频片段。"
        },
        {
          q: "可以免费试用吗？",
          a: "可以。新账户注册即获赠免费转录时长，无需绑定信用卡，即可体验完整功能。随时可升级付费套餐以解锁更高配额和高级功能。"
        },
        {
          q: "支持腾讯会议、钉钉、飞书的录音吗？",
          a: "支持。您可以从任意平台录制会议——腾讯会议、钉钉、飞书、Zoom、Google Meet 或 Microsoft Teams——然后将录音文件直接上传至 MinutesAI 进行处理。"
        }
      ]
    },
    gateModal: {
      title: "立即加入 Minutes AI",
      subtitle: "使用我们丰富的模板集合，为您的工作创建无限的结构化会议纪要。",
      createAccount: "创建账户",
      orSignIn: "或登录"
    },
    inviteModal: {
      title: "邀请好友，各得 60 分钟",
      desc: "邀请好友注册，双方各得60分钟免费时长！",
      yourCode: "您的邀请码",
      tapToCopy: "点击复制",
      copied: "已复制",
      orShareLink: "或分享链接",
      terms: "适用条款与条件",
      referrals: "目前已有 0 人加入"
    },
    share: {
      title: "分享录音",
      publicAccess: "公开访问",
      publicDesc: "公开访问，任何拥有链接的人都可以查看此逐字稿。",
      privateDesc: "仅受邀团队成员可以访问。",
      copyLink: "复制链接",
      copied: "已复制",
      preview: "预览",
      previewTooltip: "查看访客视图",
      canView: "可查看",
      canEdit: "可编辑",
      disclaimer: "分享此链接即表示您同意我们关于内容分发的服务条款。"
    },
    public: {
      sharedBy: "分享者",
      join: "加入对话",
      joinDesc: "注册以编辑、评论 and 导出此会议。",
      signupFree: "免费注册",
      wantAction: "想要查看待办事项？",
      login: "登录",
      signup: "注册"
    },
    paymentModal: {
      chooseMethod: "选择支付方式",
      detectingRegion: "正在检测您的地区...",
      regionMainland: "国内",
      regionOverseas: "国外",
      selectMethod: "请选择支付方式",
      wechatPay: "微信支付",
      alipay: "支付宝",
      wechatDesc: "使用微信扫码支付",
      alipayDesc: "跳转支付宝页面支付",
      confirmPayment: "确认支付",
      cancel: "取消",
      detecting: "检测中...",
      detectFailed: "地区检测失败，将使用默认设置",
      qrScanScan: "请使用手机扫描下方二维码完成支付",
      qrOrderNo: "订单号：",
      qrUpdateAuto: "支付完成后页面将自动更新",
      qrFinished: "我已完成支付",
      purchaseFailed: "购买失败",
      purchaseSuccess: "购买成功",
      proActivated: "已开通Pro会员",
      alipayRedirect: "已跳转支付宝支付页面，请在新窗口完成支付",
      processing: "处理中...",
      buyNow: "购买",
      purchaseSuccessDuration: "购买成功！已添加时长包",
      purchaseFailedFallback: "购买失败",
      customPlan: "专属定制方案",
      customPlanDesc: "根据团队规模、流程复杂度和合规要求定制交付。",
      accuracyGuaranteed: "精准度保障",
      bankLevelSec: "银行级加密",
      bankLevelSecSub: "AES-256 隐私防护",
      accuracySub: "精准度保障",
      contactTitle: "联系我们",
      contactDesc: "Team 方案需要专属定制，请通过邮件联系我们，我们将为您提供专属服务。",
      copy: "复制",
      copied: "已复制",
      close: "关闭",
      wechatScanPay: "微信扫码支付",
      alipayScanPay: "支付宝扫码支付",
      purchaseSuccessPro: "购买成功！已开通Pro会员",
      purchaseSuccessTeam: "购买成功！已开通Team会员",
      back: "返回",
      upgradePlan: "升级会员",
      currentPlanLabel: "当前计划",
      currentPlan: "当前方案",
      renew: "续费",
      mainlandChina: "中国大陆",
      overseas: "海外",
      scanToPay: "扫码支付",
      redirectToPay: "跳转支付",
      alipayRedirectPending: "已跳转支付宝支付页面，请在新窗口完成支付",
      enableNotifications: "开启任务通知",
      enableNotificationsDesc: "开启通知后，任务完成时会第一时间提醒你。",
      enable: "开启通知",
      notNow: "暂不开启",
      retry: "重试",
      memberMonthly: "月度配额",
      memberPermanent: "永久配额",
      memberUnlimited: "无限",
      manage: "管理权益",
      expiresLabel: "到期：",
      daysLeft: "天",
      cofounderExclusive: "内测专属",
      cofounderWelcome: "欢迎成为 ",
      cofounderLifetime: "终身权益",
      cofounderDiscount: "任意加购专享优惠",
      cofounderViewBenefits: "查看详细权益",
      cofounderMaybeLater: "稍后再说",
      cofounderDesc: "感谢你在早期加入 MinutesAI。作为前 100 名创始用户，你已自动解锁永久 20% 加购折扣权益。"
    }
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children?: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return saved === 'en' || saved === 'zh' ? saved : 'zh';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}